import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { roles } from '../../utils/roles';
import { getPermissions } from '../../lib/accessControl';
import withAuth from '../../lib/withAuth';
import useUser from '../../hooks/useUser';
import useTab from '../../hooks/useTab';
import Alert from '../../components/Alert';
import Backdrop from '@mui/material/Backdrop';
import Deals from './Deals';
import Tab from 'react-bootstrap/Tab';
import Nav from 'react-bootstrap/Nav';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import PreShipment from './PreShipment';
import PostShipment from './PostShipment';
import Loader from '../../components/Loader';
import axios from 'axios';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import { abortController } from '../../utils/abortController';
import useSessionExpire from '../../hooks/useSessionExpire';
import { useDispatch } from 'react-redux';
import { resetPageFilters } from '../../store/slices/filters';

const BusinessUnit = ({ logOut = () => { } }) => {

  const {
    activeTab,
    selectedTab,
    selectedDeal,
    selectedPFI,
    selectedTranche,
    setActiveTab,
    setSelectedTab,
    setSelectedDeal,
    setSelectedPFI,
    setSelectedTranche,
    selectedLinkedDeal = null,
    selectedLinkedPFI = null,
    setSelectedLinkedDeal = () => { },
    setSelectedLinkedPFI = () => { },
    setIsNewPFI = () => { }
  } = useTab();

  const { pathname } = useLocation();
  const triggerSessionExpire = useSessionExpire();

  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const location = useLocation();
  const { selectedBusinessUnit, dealState } = location.state || {};

  // Store initial IDs to maintain consistent positioning
  const [initialSelectedDealId, setInitialSelectedDealId] = useState(null);
  const [initialLinkedDealId, setInitialLinkedDealId] = useState(null);



  // inside BusinessUnit component
  const dispatch = useDispatch();
  const previousBusinessUnitId = useRef(selectedBusinessUnit?.id);

  useEffect(() => {
    const currentId = selectedBusinessUnit?.id;
    if (
      previousBusinessUnitId.current !== undefined &&
      previousBusinessUnitId.current !== currentId
    ) {
      dispatch(resetPageFilters({ pageKey: `deals-${selectedBusinessUnit?.id || "default"}` })); 
    }
    previousBusinessUnitId.current = currentId;
  }, [selectedBusinessUnit?.id, dispatch]);

  // Capture initial deal IDs on first render
  useEffect(() => {
    if (selectedDeal?.id && initialSelectedDealId === null) {
      setInitialSelectedDealId(selectedDeal.id);
    }
  }, [selectedDeal?.id, initialSelectedDealId]);

  useEffect(() => {
    if (selectedLinkedDeal?.id && initialLinkedDealId === null) {
      setInitialLinkedDealId(selectedLinkedDeal.id);
    }
  }, [selectedLinkedDeal?.id, initialLinkedDealId]);

  // Determine which deal should be on left/right based on initial IDs
  const { leftDealTab, rightDealTab, isLeftDealActive, leftDealData, rightDealData } = useMemo(() => {
    let leftTab = "";
    let rightTab = "";
    let leftIsActive = false;
    let leftData = null;
    let rightData = null;

    // If we have both deals
    if (selectedDeal && selectedLinkedDeal) {
      // Check which deal matches the initial selected deal ID
      if (selectedDeal.id === initialSelectedDealId) {
        // Current deal is the original, so it goes on left
        leftTab = selectedDeal.value + (selectedPFI ? selectedPFI?.value?.slice(selectedDeal.value.length) : "");
        rightTab = selectedLinkedDeal.value + (selectedLinkedPFI?.value ? selectedLinkedPFI?.value?.slice(selectedLinkedDeal.value.length) : "");
        leftIsActive = true;
        leftData = { deal: selectedDeal, pfi: selectedPFI };
        rightData = { deal: selectedLinkedDeal, pfi: selectedLinkedPFI };
      } else if (selectedLinkedDeal.id === initialSelectedDealId) {
        // Linked deal is the original, so it goes on left
        leftTab = selectedLinkedDeal.value + (selectedLinkedPFI?.value ? selectedLinkedPFI?.value?.slice(selectedLinkedDeal.value.length) : "");
        rightTab = selectedDeal.value + (selectedPFI ? selectedPFI?.value?.slice(selectedDeal.value.length) : "");
        leftIsActive = false;
        leftData = { deal: selectedLinkedDeal, pfi: selectedLinkedPFI };
        rightData = { deal: selectedDeal, pfi: selectedPFI };
      } else {
        // Fallback: selected deal on left
        leftTab = selectedDeal.value + (selectedPFI ? selectedPFI?.value?.slice(selectedDeal.value.length) : "");
        rightTab = selectedLinkedDeal.value + (selectedLinkedPFI?.value ? selectedLinkedPFI?.value?.slice(selectedLinkedDeal.value.length) : "");
        leftIsActive = true;
        leftData = { deal: selectedDeal, pfi: selectedPFI };
        rightData = { deal: selectedLinkedDeal, pfi: selectedLinkedPFI };
      }
    }
    // If we only have selected deal
    else if (selectedDeal) {
      if (initialSelectedDealId && selectedDeal.id === initialSelectedDealId) {
        leftTab = selectedDeal.value + (selectedPFI ? selectedPFI?.value?.slice(selectedDeal.value.length) : "");
        leftIsActive = true;
        leftData = { deal: selectedDeal, pfi: selectedPFI };
      } else if (initialLinkedDealId && selectedDeal.id === initialLinkedDealId) {
        rightTab = selectedDeal.value + (selectedPFI ? selectedPFI?.value?.slice(selectedDeal.value.length) : "");
        leftIsActive = false;
        rightData = { deal: selectedDeal, pfi: selectedPFI };
      } else {
        leftTab = selectedDeal.value + (selectedPFI ? selectedPFI?.value?.slice(selectedDeal.value.length) : "");
        leftIsActive = true;
        leftData = { deal: selectedDeal, pfi: selectedPFI };
      }
    }
    // If we only have linked deal
    else if (selectedLinkedDeal) {
      if (initialLinkedDealId && selectedLinkedDeal.id === initialLinkedDealId) {
        rightTab = selectedLinkedDeal.value + (selectedLinkedPFI ? selectedLinkedPFI?.value?.slice(selectedLinkedDeal.value.length) : "");
        leftIsActive = false;
        rightData = { deal: selectedLinkedDeal, pfi: selectedLinkedPFI };
      } else if (initialSelectedDealId && selectedLinkedDeal.id === initialSelectedDealId) {
        leftTab = selectedLinkedDeal.value + (selectedLinkedPFI ? selectedLinkedPFI?.value?.slice(selectedLinkedDeal.value.length) : "");
        leftIsActive = true;
        leftData = { deal: selectedLinkedDeal, pfi: selectedLinkedPFI };
      } else {
        leftTab = selectedLinkedDeal.value + (selectedLinkedPFI ? selectedLinkedPFI?.value?.slice(selectedLinkedDeal.value.length) : "");
        leftIsActive = false;
        leftData = { deal: selectedLinkedDeal, pfi: selectedLinkedPFI };
      }
    }

    return {
      leftDealTab: leftTab,
      rightDealTab: rightTab,
      isLeftDealActive: leftIsActive,
      leftDealData: leftData,
      rightDealData: rightData
    };
  }, [selectedDeal, selectedPFI, selectedLinkedDeal, selectedLinkedPFI, initialSelectedDealId, initialLinkedDealId]);

  const { isFertilizersAndPetroChemicalsDeal, isFoodsOrPolymersDeal, isFoodsPolymersOrSBLCDeal, isSBLCDeal } = useMemo(() => {
    const id = selectedBusinessUnit?.id;
    return {
      isFertilizersAndPetroChemicalsDeal: [2, 3, 5].includes(id),
      isFoodsOrPolymersDeal: [1, 4].includes(id),
      isFoodsPolymersOrSBLCDeal: [1, 4, 6].includes(id),
      isSBLCDeal: id === 6
    };
  }, [selectedBusinessUnit]);

  const debounce = require('lodash.debounce');
  const currentUser = useUser();
  const { isWritable = false } = getPermissions(currentUser?.role, location?.pathname);

  // LINKED DEAL VARIABLES
  const [showNoPFIDialog, setShowNoPFIDialog] = useState(false);
  const [isPeriodLocked, setIsPeriodLocked] = useState(selectedPFI?.period_lock);
  const [isUpdated, setIsUpdated] = useState(false);
  const [isDocumentToBeGenerated, setIsDocumentToBeGenerated] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const preShipmentRef = useRef(null);
  const postShipmentRef = useRef(null);
  const debouncedHandleTabChange = useRef(null);

  // Set the states from navigation when component mounts
  useEffect(() => {
    if (dealState) {
      setSelectedDeal(dealState.selectedDeal);
      setSelectedTranche(dealState.selectedTranche);
      setSelectedPFI(dealState.selectedPFI);
      setActiveTab(dealState.activeTab);
    }
  }, [dealState, setSelectedDeal, setSelectedPFI, setActiveTab, setSelectedTranche]);

  // Fetch Linked Deal Reference (First API Call)
  useEffect(() => {
    const fetchLinkedDealReference = async () => {
      setShowAlert(false);
      setAlertMessage("");
      setAlertType("");

      try {
        const response = await axios({
          method: "post",
          url: "/api/GetLinkedDealReference",
          data: {
            pathname,
            deal_no: selectedDeal?.id,
            business_unit_id: selectedBusinessUnit?.id
          },
          signal: abortController.signal,
        });

        const { status, data } = response;
        if (status === 200) {
          setSelectedLinkedDeal(data?.data);
        }
      } catch (error) {
        console.error("Get Linked Deal Reference API: ", error);
        if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;

        const status = error?.response?.status;
        if (status === 403) {
          triggerSessionExpire();
        } else {
          setAlertMessage(
            status === 401
              ? "Unauthorized access."
              : status === 429
                ? "Request limit exceeded."
                : "An error occurred while fetching linked deal reference."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) {
            setTimeout(logOut, 3000);
          }
        }
      }
    };

    if (selectedDeal?.id) {
      fetchLinkedDealReference();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeal?.id]);

  // Handle clicking on a deal tab to switch
  const handleDealTabClick = async (clickedDealData) => {
    if (!clickedDealData) return;
    // If clicking on already active deal, do nothing
    const isClickingActiveDeal = clickedDealData.deal.id === selectedDeal?.id;
    if (isClickingActiveDeal) return;

    // Check if the clicked deal has PFIs
    setIsLoading(true);
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");

    try {
      const response = await axios({
        method: "post",
        url: "/api/GetDealPFIListForAutoPopulate",
        data: {
          pathname,
          Deal_Id: clickedDealData.deal.id,
          Business_Unit_Id: selectedBusinessUnit?.id
        },
        signal: abortController.signal,
      });

      const { status, data } = response;

      if (status === 200) {
        const pfiList = data?.deal_pfi || [];

        if (pfiList.length > 0) {
          let pfiToUse = pfiList[0]; // default fallback

          if (clickedDealData.pfi?.value) {
            const wantedSuffix = clickedDealData.pfi?.value?.slice(-1); // "A", "B", "C" etc.

            const matchingPfi = pfiList.find(pfi =>
              pfi?.value && pfi?.value?.slice(-1) === wantedSuffix
            );

            if (matchingPfi) {
              pfiToUse = matchingPfi;
            }
          }
          handleDealSwitch({ ...clickedDealData.deal, is_vantira_deal: Boolean(!selectedDeal.is_vantira_deal), is_route_linked_deal: Boolean(selectedDeal?.is_route_linked_deal) }, pfiToUse);
        } else {
          // No PFIs found - show dialog to add one
          setShowNoPFIDialog(true);
        }
      } else {
        setAlertMessage("Failed to fetch PFIs for the deal.");
        setAlertType("error");
        setShowAlert(true);
      }
    } catch (error) {
      console.error("Fetch Deal PFIs Error:", error);

      if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
        return;
      }

      const status = error?.response?.status;

      if (status === 403) {
        triggerSessionExpire();
      } else {
        setAlertMessage(
          status === 401
            ? "Unauthorized access."
            : status === 429
              ? "Request limit exceeded."
              : "Failed to fetch PFIs for the deal."
        );
        setAlertType("error");
        setShowAlert(true);

        if (status === 429) {
          setTimeout(logOut, 3000);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDealSwitch = (newSelectedDeal, newSelectedPFI) => {
    // Swap the deals
    const tempDeal = selectedDeal;
    const tempPFI = selectedPFI;

    setSelectedDeal(newSelectedDeal);
    setSelectedPFI(newSelectedPFI);

    setSelectedLinkedDeal({ ...tempDeal, is_route_linked_deal: Boolean(newSelectedDeal?.is_route_linked_deal) });
    setSelectedLinkedPFI(tempPFI);

    setSelectedTranche(null);
    setShowNoPFIDialog(false);
  };

  const handleAddDealPFI = async () => {
    setShowNoPFIDialog(false);
    setIsLoading(true);
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");
    setSelectedTranche(null);
    setIsNewPFI(true);

    // Determine which deal needs a new PFI (the one that was clicked)
    const dealNeedingPFI = isLeftDealActive ? rightDealData?.deal : leftDealData?.deal;

    try {
      const response = await axios({
        method: "POST",
        url: "/api/AddDealPFI",
        data: {
          business_unit_id: selectedBusinessUnit?.id,
          deal_no: dealNeedingPFI?.id,
          user_id: currentUser?.id
        },
        signal: abortController.signal
      });

      const { status, data } = response;

      if (status === 200) {
        const newPFI = {
          id: data?.id,
          value: dealNeedingPFI?.value + data?.value
        };

        // Swap deals after creating new PFI
        handleDealSwitch(dealNeedingPFI, newPFI);

        setActiveTab("Pre-Shipment");
      } else {
        setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
        setAlertType("error");
        setShowAlert(true);
      }
    } catch (error) {
      console.error("Generate PFI Api: ", error);

      if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
        return;
      }

      const status = error?.response?.status;

      if (status === 403) {
        triggerSessionExpire();
      } else {
        setAlertMessage(
          status === 401
            ? "Unauthorized access. You do not have the required permissions to perform this action."
            : status === 429
              ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
              : "An error occurred while processing your request. Please try again later or contact the site administrator."
        );
        setAlertType("error");
        setShowAlert(true);

        if (status === 429) {
          setTimeout(logOut, 3000);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    debouncedHandleTabChange.current = debounce((tab) => {
      if (isUpdated) {
        setSelectedTab(tab);
        setShowConfirmationDialog(true);
      } else {
        handleConfirmTabChange(tab);
      }
    }, 200);

    return () => {
      debouncedHandleTabChange.current.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdated]);

  const handleConfirmTabChange = (tab) => {
    if (tab === "Deals") {
      setSelectedDeal(null);
      setSelectedPFI(null);
      setSelectedLinkedDeal(null);
      setSelectedLinkedPFI(null);
      setInitialSelectedDealId(null);
      setInitialLinkedDealId(null);
    }

    setActiveTab(() => tab);
    setSelectedTab((previousSelectedTab) => (previousSelectedTab === tab ? previousSelectedTab : null));
    setIsUpdated(false);
    setIsDocumentToBeGenerated(false);
  };

  const handleTabChange = useCallback((tab) => {
    if (tab === "Deals" || selectedPFI) {
      debouncedHandleTabChange.current(tab);
    }
  }, [selectedPFI]);

  const handleSaveChanges = async () => {
    setShowConfirmationDialog(false);

    try {
      let submitResult = false;

      switch (activeTab) {
        case "Pre-Shipment":
          if (preShipmentRef.current) {
            submitResult = await preShipmentRef.current.handleSubmit();
          }
          break;
        case "Post-Shipment":
          if (postShipmentRef.current) {
            submitResult = await postShipmentRef.current.handleSubmit();
          }
          break;
        default:
          break;
      }

      // Only change tab if submission was successful and not waiting for popup
      // submitResult will be false if we're showing the linked deal override popup
      if (submitResult !== false && !isDocumentToBeGenerated) {
        handleConfirmTabChange(selectedTab);
      }
    } catch (error) {
      console.error("Save changes api failed due to unknown reasons.");
    }
  };

  const handleDiscardChanges = () => {
    setShowConfirmationDialog(false);
    handleConfirmTabChange(selectedTab);
  };

  const handleCloseDialog = () => {
    setShowConfirmationDialog(false);
  };

  const handleCloseNoPFIDialog = () => {
    setShowNoPFIDialog(false);
  };

  return (
    <div className="business-unit container position-relative">
      <Alert
        show={showAlert}
        message={alertMessage}
        type={alertType}
        setShow={setShowAlert}
      />

      {/* Save Changes Dialog */}
      <Dialog open={showConfirmationDialog} onClose={handleCloseDialog}>
        <DialogContent>
          <DialogContentText className="text-center">
            Save changes before proceeding?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            className="btn btn-secondary custom-btn"
            onClick={handleSaveChanges}
          >
            Save
          </button>
          {isDocumentToBeGenerated ? (
            <button
              type="button"
              className="btn bg-secondary custom-btn"
              onClick={handleCloseDialog}
            >
              Cancel
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-danger custom-btn"
              onClick={handleDiscardChanges}
            >
              Discard
            </button>
          )}
        </DialogActions>
      </Dialog>

      {/* No PFI Available Dialog */}
      <Dialog open={showNoPFIDialog} onClose={handleCloseNoPFIDialog}>
        <DialogContent>
          <DialogContentText className="text-center">
            This deal has no PFIs. Would you like to create one?
          </DialogContentText>
        </DialogContent>
        <DialogActions className="d-flex justify-content-center">
          <button
            type="button"
            className="btn btn-danger custom-btn"
            onClick={handleCloseNoPFIDialog}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-secondary custom-btn"
            onClick={handleAddDealPFI}
          >
            Add PFI
          </button>
        </DialogActions>
      </Dialog>

      <div>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <Loader />
        </Backdrop>

        <Tab.Container activeKey={activeTab} defaultActiveKey="Deals">
          <Nav variant="tabs" className="deal-tabs w-100 d-flex align-items-center justify-content-between">
            <div className='d-flex align-items-center' style={{ gap: "5px" }}>
              {/* Left Deal Tab - Always maintains its position */}

              {leftDealTab && activeTab !== "Deals" && (
                <Nav.Item>
                  <Nav.Link
                    eventKey="left-deal-tab"
                    active={isLeftDealActive}
                    onClick={() => handleDealTabClick(leftDealData)}
                    style={{ cursor: 'pointer' }}
                  >
                    {leftDealTab}
                  </Nav.Link>
                </Nav.Item>
              )}

              {/* Link Icon - Only show when we have both deals */}
              {leftDealTab && rightDealTab && activeTab !== "Deals" && (
                <InsertLinkIcon
                  className='d-flex align-items-center'
                  fontSize='medium'
                  sx={{ color: '#0C969C' }}
                />
              )}

              {/* Right Deal Tab - Always maintains its position */}
              {rightDealTab && activeTab !== "Deals" && (
                <Nav.Item>
                  <Nav.Link
                    eventKey="right-deal-tab"
                    active={!isLeftDealActive}
                    onClick={() => handleDealTabClick(rightDealData)}
                    style={{ cursor: 'pointer' }}
                  >
                    {rightDealTab}
                  </Nav.Link>
                </Nav.Item>
              )}
            </div>

            <Nav.Item className="d-flex align-items-center">
              <Nav.Item>
                <Nav.Link
                  eventKey="Deals"
                  disabled={isLoading && activeTab !== "Deals"}
                  onClick={() => handleTabChange("Deals")}
                >
                  Deals
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  eventKey="Pre-Shipment"
                  disabled={isLoading && activeTab !== "Pre-Shipment"}
                  onClick={() => handleTabChange("Pre-Shipment")}
                >
                  Pre-Shipment
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  eventKey="Post-Shipment"
                  disabled={isLoading && activeTab !== "Post-Shipment"}
                  onClick={() => handleTabChange("Post-Shipment")}
                >
                  Post-Shipment
                </Nav.Link>
              </Nav.Item>
            </Nav.Item>
          </Nav>
        </Tab.Container>

        <div className={`content-container p-0 d-flex flex-column deals ${selectedDeal ? "deal-selected" : ""}`}>
          {(activeTab === "Deals") ? (
            <Deals
              isWritable={isWritable}
              isLoading={isLoading}
              selectedBusinessUnit={selectedBusinessUnit?.id}
              isFertilizersAndPetroChemicalsDeal={isFertilizersAndPetroChemicalsDeal}
              isFoodsOrPolymersDeal={isFoodsOrPolymersDeal}
              setActiveTab={setActiveTab}
              setSelectedDeal={setSelectedDeal}
              setSelectedPFI={setSelectedPFI}
              isPeriodLocked={isPeriodLocked}
              setIsPeriodLocked={setIsPeriodLocked}
              setIsLoading={setIsLoading}
              setShowAlert={setShowAlert}
              setAlertMessage={setAlertMessage}
              setAlertType={setAlertType}
              logOut={logOut}
            />
          ) : (activeTab === "Pre-Shipment") ? (
            <PreShipment
              ref={preShipmentRef}
              isWritable={isWritable}
              isLoading={isLoading}
              isFertilizersAndPetroChemicalsDeal={isFertilizersAndPetroChemicalsDeal}
              isFoodsOrPolymersDeal={isFoodsOrPolymersDeal}
              isFoodsPolymersOrSBLCDeal={isFoodsPolymersOrSBLCDeal}
              isSBLCDeal={isSBLCDeal}
              selectedBusinessUnit={selectedBusinessUnit?.id}
              selectedDeal={selectedDeal}
              selectedPFI={selectedPFI}
              selectedLinkedDeal={selectedLinkedDeal}
              selectedLinkedPFI={selectedLinkedPFI}
              isPeriodLocked={isPeriodLocked}
              setIsPeriodLocked={setIsPeriodLocked}
              setIsUpdated={setIsUpdated}
              selectedTab={activeTab}
              setActiveTab={setActiveTab}
              setIsLoading={setIsLoading}
              setShowAlert={setShowAlert}
              setAlertMessage={setAlertMessage}
              setAlertType={setAlertType}
              logOut={logOut}
            />
          ) : (activeTab === "Post-Shipment") && (
            <PostShipment
              ref={postShipmentRef}
              isWritable={isWritable}
              isLoading={isLoading}
              isFoodsOrPolymersDeal={isFoodsOrPolymersDeal}
              isFoodsPolymersOrSBLCDeal={isFoodsPolymersOrSBLCDeal}
              isSBLCDeal={isSBLCDeal}
              isUpdated={isUpdated}
              selectedBusinessUnit={selectedBusinessUnit?.id}
              selectedDeal={selectedDeal}
              selectedPFI={selectedPFI}
              selectedTranche={selectedTranche}
              selectedLinkedDeal={selectedLinkedDeal}
              selectedLinkedPFI={selectedLinkedPFI}
              setIsUpdated={setIsUpdated}
              setIsDocumentToBeGenerated={setIsDocumentToBeGenerated}
              setSelectedTranche={setSelectedTranche}
              setShowConfirmationDialog={setShowConfirmationDialog}
              setIsLoading={setIsLoading}
              setShowAlert={setShowAlert}
              setAlertMessage={setAlertMessage}
              setAlertType={setAlertType}
              logOut={logOut}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default withAuth(BusinessUnit)([
  roles[2], roles[3], roles[4], roles[5], roles[6], roles[7], roles[8],
  roles[10], roles[11], roles[12], roles[13], roles[14], roles[15],
  roles[16], roles[17], roles[18], roles[19]
]);